import $ from 'jquery';

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
} from '@angular/core';
import { Params } from '@angular/router';

import { Session } from '../services/session';
import { GroupsFilter } from '../services/groups-filter';
import { PERMISSIONS } from './../lib/permissions';

export enum FEATURE {
  FLEET_VIEW,
  SAFETY,
}

export interface ICrossLinkEntity {
  id: number;
  groupIds?: number[];
  number?: string;
  name?: string;
  email?: string;
  deactivatedAt?: string;
  companyId?: number;
}

export type AllowedType = 'asset' | 'driver' | 'vehicle' | 'group' | 'trip';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'cross-link',
  styleUrls: ['./cross-link.component.scss'],
  templateUrl: './cross-link.component.html',
})
export class CrossLinkComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() public entity: ICrossLinkEntity;
  @Input() public type: AllowedType;
  @Input() public noGroupsCheck;
  @Input() public params;
  @Input() public featureType: FEATURE = FEATURE.FLEET_VIEW;
  @Input() public routeParams: Params = {};
  @Input() public target: string = '';
  @Input() public deactivated: boolean = false;
  @Output() public popupEnabled = new EventEmitter();
  public isDispatcher;
  public isLink;
  public isFeatureAllowed;
  public isInGroup;
  public link;
  public text;
  private $el;
  private el;
  private content;
  private typeToFeature: { [key in AllowedType]: PERMISSIONS } = {
    vehicle: PERMISSIONS.VEHICLES_READ,
    asset: PERMISSIONS.ASSETS_READ,
    driver: PERMISSIONS.DRIVERS_READ,
    group: PERMISSIONS.GROUPS_MANAGE,
    trip: PERMISSIONS.TRIPS_MANAGE,
  };

  constructor(
    elRef: ElementRef,
    private session: Session,
    private groupsFilter: GroupsFilter,
  ) {
    this.el = elRef.nativeElement;
    this.$el = $(this.el);
  }

  public ngOnChanges() {
    if (!this.entity) return;
    const session = this.session;
    const type: AllowedType = this.type;
    const entity = this.entity;
    const id = entity.id;
    const groupIds = entity.groupIds || ((type === 'group' && entity.id) ? [entity.id] : []);
    const selectedGroupIds = this.groupsFilter.value;
    const noGroupsCheck = this.noGroupsCheck;

    const isDispatcher = this.isDispatcher = session.isDispatcher();
    const isLink = this.isLink = Boolean(entity.id);
    const featureType: string = this.typeToFeature[type] || PERMISSIONS.DRIVERS_READ;
    const isFeatureAllowed = this.isFeatureAllowed = session.isFeatureAllowed(featureType);
    const isInSelectedGroups = session.isInSelectedGroups(groupIds, this.typeToFeature[type]) || noGroupsCheck;
    const isInAllowedGroups = session.isInAllowedGroups(groupIds, this.typeToFeature[type]) || noGroupsCheck;

    this.isInGroup = isInSelectedGroups;

    let link: string = this.getDriverProfileLink();
    this.text = entity.name;

    if (type === 'vehicle') {
      link = '/fleetview/vehicles';
      this.text = entity.number || entity.name;
    } else if (type === 'asset') {
      link = '/fleetview/assets';
    } else if (type === 'group') {
      link = '/admin/groups';
    } else if (type === 'trip') {
      link = '/fleetview/trips/trips/';
    }

    if (this.deactivated) {
      this.text = $localize`${this.text} (Deactivated)`;
    }

    this.link = [link, id, this.routeParams];

    if (isDispatcher) {
      let content;
      if (!isLink) {
        content = $localize`This ${type} was manually entered and does not have a linked profile.`;
      } else if (!isFeatureAllowed) {
        content = $localize`You do not have access to view ${type} profile.`;
      } else if (!isInAllowedGroups) {
        content = $localize`This ${type} is not included in the groups that you have access to.`;
      } else if (!isInSelectedGroups) {
        const group = selectedGroupIds.length > 1 ? $localize`groups` : $localize`group`;
        // tslint:disable-next-line: max-line-length
        content = $localize`This ${type} is not in the ${group} you have selected. Update your group selection to view this ${type}.`;
      }
      this.content = content;
    }
  }

  public ngAfterViewInit() {
    const content = this.content;
    if (content) {
      /*
       * We are forced to move this if block here to delay the event emission as much as possible
       * Also added a setTimeout to delay it further, because this event emission triggers detectChanges()
       * in other parts of the code which causes us to hit this bug: https://github.com/angular/angular/issues/17035
       * Fix for the bug is in Angular 5.1: https://github.com/angular/angular/pull/20258
       * We should move this if block back to ngOnInit when we upgrade beyond Angular 5.1
       */
      setTimeout(() => this.popupEnabled.emit(true), 0);
      this.$el.popup({
        content,
        variation: 'inverted',
      });
    }
  }

  public ngOnDestroy() {
    if (!this.isLink) {
      this.$el.popup('destroy');
    }
  }

  get isEnabledLink(): boolean {
    return this.isLink && this.isInGroup && this.isFeatureAllowed && !this.deactivated;
  }

  get isDisabledLink(): boolean {
    return this.isLink && (!this.isInGroup || !this.isFeatureAllowed || this.deactivated);
  }

  private getDriverProfileLink(): string {
    switch (this.featureType) {
    case FEATURE.SAFETY:
      return '/safety/drivers';
    default:
      return '/fleetview/drivers/summary';
    }
  }
}
